import { ChangeDetectionStrategy, ChangeDetectorRef, Component, effect, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { IndexTemplateComponent } from '@bisual/ngx-index-template';
import { DatePipe } from '@angular/common';
import { MeetingRecord, MeetingRecordAssistedListResourceIndicatorStatus } from 'src/app/models/meeting-record.interface';
import { ApiService, EmployeeService, UtilsService } from 'src/app/services';
import { buildMeetingRecordDetailRoute } from 'src/app/components/main/shared/meeting-record/meeting-record.component';

export interface MeetingRecordAssistedListResourceIndicatorView {
  status: MeetingRecordAssistedListResourceIndicatorStatus;
  tooltip: string;
  is_available: boolean;
  is_in_progress: boolean;
  is_failed: boolean;
  is_visible: boolean;
}

interface AssistedMeetingRecordRow extends MeetingRecord {
  meeting_type_label: string;
  meeting_type_chip_classes: string;
  context_label: string;
  acta_route: string[] | null;
  server_processing_indicator: MeetingRecordAssistedListResourceIndicatorView;
  youtube_upload_indicator: MeetingRecordAssistedListResourceIndicatorView;
  transcription_indicator: MeetingRecordAssistedListResourceIndicatorView;
}

@Component({
  selector: 'app-assisted-meeting-records',
  templateUrl: './assisted-meeting-records.component.html',
  styleUrls: ['./assisted-meeting-records.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatPaginatorModule,
    MatTableModule,
    MatChipsModule,
    MatTooltipModule,
    MatSelectModule,
    MatOptionModule,
    DatePipe,
  ],
})
export class AssistedMeetingRecordsComponent extends IndexTemplateComponent {
  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  private api = inject(ApiService);
  private change_detector_ref = inject(ChangeDetectorRef);
  private employee_service = inject(EmployeeService);

  protected active_company_products: {
    company_product_id: number;
    company_product_name: string;
    department_category_id: number;
    slug: string;
  }[] = [];

  protected displayed_columns: string[] = ['title', 'date', 'context', 'resources', 'actions'];
  protected data_source = new MatTableDataSource<AssistedMeetingRecordRow>([]);

  constructor() {
    super(inject(Router), inject(FormBuilder), inject(ActivatedRoute), inject(UtilsService));
    this.initActiveCompanyProductsEffect();
  }

  override filterFormExtraParams = {
    search: [''],
    company_product_id: [this.activatedRoute.snapshot.queryParamMap.get('company_product_id') ?? ''],
  };

  override fetchData(): void {
    const form_value = this.filterForm.value;
    const params: Record<string, unknown> = {
      page: this.pageIndex,
      per_page: this.pageSize,
      order_by: 'date:desc',
      search: form_value.search,
    };

    if (form_value.company_product_id) {
      params.company_product_id = form_value.company_product_id;
    }

    this.api.getAssistedMeetingRecords(params).subscribe(response => {
      this.data_source.data = response.data.map(meeting_record => this.serializeMeetingRecord(meeting_record));
      this.setMetadata(+response.meta.total, response.meta.current_page - 1, response.meta.per_page);
      this.change_detector_ref.markForCheck();
    });
  }

  protected navigateToActa(meeting_record: AssistedMeetingRecordRow): void {
    if (!meeting_record.acta_route) {
      return;
    }

    void this.router.navigate(meeting_record.acta_route, {
      queryParams: { from: 'assisted-meeting-records' },
    });
  }

  private serializeMeetingRecord(meeting_record: MeetingRecord): AssistedMeetingRecordRow {
    return {
      ...meeting_record,
      meeting_type_label: this.buildMeetingTypeLabel(meeting_record),
      meeting_type_chip_classes: `meeting-type-chip ${this.buildMeetingTypeChipClass(meeting_record)}`,
      context_label: this.buildContextLabel(meeting_record),
      acta_route: buildMeetingRecordDetailRoute(meeting_record),
      server_processing_indicator: serializeMeetingRecordAssistedListResourceIndicator(
        meeting_record.server_processing_indicator_status,
        meeting_record.server_processing_indicator_tooltip
      ),
      youtube_upload_indicator: serializeMeetingRecordAssistedListResourceIndicator(
        meeting_record.youtube_upload_indicator_status,
        meeting_record.youtube_upload_indicator_tooltip,
        true
      ),
      transcription_indicator: serializeMeetingRecordAssistedListResourceIndicator(
        meeting_record.transcription_indicator_status,
        meeting_record.transcription_indicator_tooltip
      ),
    };
  }

  private buildMeetingTypeLabel(meeting_record: MeetingRecord): string {
    switch (meeting_record.meetingable_type) {
      case 'App\\Models\\CompanyProduct':
        return 'Producto';
      case 'App\\Models\\JobVacancyCandidatePhaseProgress':
        return 'Entrevista';
      case 'App\\Models\\User':
        return 'Reunión de empleado';
      case 'App\\Models\\PotentialClient':
        return 'Cliente Potencial';
      default:
        return 'Reunión';
    }
  }

  private buildMeetingTypeChipClass(meeting_record: MeetingRecord): string {
    switch (meeting_record.meetingable_type) {
      case 'App\\Models\\CompanyProduct':
        return 'meeting-type-chip-company_product';
      case 'App\\Models\\JobVacancyCandidatePhaseProgress':
        return 'meeting-type-chip-interview';
      case 'App\\Models\\User':
        return 'meeting-type-chip-employee';
      case 'App\\Models\\PotentialClient':
        return 'meeting-type-chip-potential-client';
      default:
        return 'meeting-type-chip-default';
    }
  }

  private buildContextLabel(meeting_record: MeetingRecord): string {
    const meetingable = meeting_record.meetingable as { title?: string; full_name?: string; company_name?: string } | undefined;

    if (!meetingable) {
      return 'Reunión';
    }

    if (meeting_record.meetingable_type === 'App\\Models\\CompanyProduct') {
      return meetingable.title ?? 'Producto';
    }

    if (meeting_record.meetingable_type === 'App\\Models\\PotentialClient') {
      return meetingable.company_name ?? meetingable.full_name ?? 'Cliente potencial';
    }

    return meetingable.full_name ?? meetingable.title ?? 'Reunión';
  }

  private initActiveCompanyProductsEffect(): void {
    effect(() => {
      this.active_company_products = this.employee_service.active_company_products_signal();
      this.change_detector_ref.markForCheck();
    });
  }
}

export function serializeMeetingRecordAssistedListResourceIndicator(
  status: MeetingRecordAssistedListResourceIndicatorStatus | undefined,
  tooltip: string | undefined,
  hide_when_not_applicable = false
): MeetingRecordAssistedListResourceIndicatorView {
  const resolved_status = status ?? 'unavailable';
  const resolved_tooltip = tooltip ?? '';

  return {
    status: resolved_status,
    tooltip: resolved_tooltip,
    is_available: resolved_status === 'completed',
    is_in_progress: resolved_status === 'in_progress',
    is_failed: resolved_status === 'failed',
    is_visible: hide_when_not_applicable ? resolved_status !== 'not_applicable' : true,
  };
}
